import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package,
  Plus,
  Download,
  Upload,
  Filter,
  X,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';

// Components
import { ProductsTable } from './components/ProductsTable';
import { ProductForm } from './components/ProductForm';
import { ProductFilters } from './components/ProductFilters';
import { BulkActionsBar } from './components/BulkActionsBar';
import { ImportExport } from './components/ImportExport';
import { QuickPrintModal } from './components/QuickPrintModal';

// Hooks
import {
  useProducts,
  useCreateProduct,
  useUpdateProduct,
  useDeleteProduct,
  useDuplicateProduct,
} from './hooks/useProducts';
import { useCategories } from './hooks/useCategories';
import {
  useBulkUpdate,
  useBulkDelete,
  useBulkMove,
  useBulkDuplicate,
  useImportProducts,
  useExportProducts,
  useDownloadTemplate,
} from './hooks/useBulkActions';

// Types
import type { Product, ProductFilters as Filters, Store } from './types';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

interface ProductsPageProps {
  defaultStoreId?: string; // Pre-filter by store and use as default for new products
}

export default function ProductsPage({ defaultStoreId }: ProductsPageProps = {}) {
  const navigate = useNavigate();
  
  // State - initialize with defaultStoreId if provided
  const [filters, setFilters] = useState<Filters>({ storeId: defaultStoreId });
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showImportExport, setShowImportExport] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  
  // Quick Print Modal state
  const [showQuickPrint, setShowQuickPrint] = useState(false);
  const [productToPrint, setProductToPrint] = useState<Product | null>(null);

  // Fetch stores
  const { data: storesData } = useQuery({
    queryKey: ['stores'],
    queryFn: () => api.get<{ stores: Store[] }>('/storefront/stores'),
  });
  const stores = storesData?.stores || [];

  // Fetch categories
  const { data: categoriesData } = useCategories(filters.storeId);
  const categories = categoriesData?.categories || [];

  // Fetch products
  const { data: productsData, isLoading } = useProducts({
    ...filters,
    sortBy: sortBy as any,
    sortOrder,
    limit: pageSize,
  });

  const products = productsData?.products || [];
  const total = productsData?.pagination?.total || 0;

  // Mutations
  const createMutation = useCreateProduct();
  const updateMutation = useUpdateProduct();
  const deleteMutation = useDeleteProduct();
  const duplicateMutation = useDuplicateProduct();
  const bulkUpdateMutation = useBulkUpdate();
  const bulkDeleteMutation = useBulkDelete();
  const bulkMoveMutation = useBulkMove();
  const bulkDuplicateMutation = useBulkDuplicate();
  const importMutation = useImportProducts();
  const exportMutation = useExportProducts();
  const downloadTemplateMutation = useDownloadTemplate();

  // Handlers
  const handleSort = useCallback((field: string) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  }, [sortBy]);

  const handleSelectAll = useCallback(
    (selected: boolean) => {
      if (selected) {
        setSelectedIds(products.map((p) => p.id));
      } else {
        setSelectedIds([]);
      }
    },
    [products]
  );

  const handleSelectOne = useCallback((id: string, selected: boolean) => {
    if (selected) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((i) => i !== id));
    }
  }, []);

  const handleEdit = useCallback((product: Product) => {
    setEditingProduct(product);
    setShowForm(true);
  }, []);

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await deleteMutation.mutateAsync(id);
        toast.success('Product deleted');
        setSelectedIds((prev) => prev.filter((i) => i !== id));
      } catch (error) {
        toast.error('Failed to delete product');
      }
    },
    [deleteMutation]
  );

  const handleDuplicate = useCallback(
    async (id: string) => {
      try {
        await duplicateMutation.mutateAsync(id);
        toast.success('Product duplicated');
      } catch (error) {
        toast.error('Failed to duplicate product');
      }
    },
    [duplicateMutation]
  );

  const handleSave = useCallback(
    async (data: Partial<Product>) => {
      try {
        if (editingProduct) {
          await updateMutation.mutateAsync({ id: editingProduct.id, data });
          toast.success('Product updated');
          setShowForm(false);
          setEditingProduct(null);
        } else {
          await createMutation.mutateAsync(data);
          toast.success('Product created');
          setShowForm(false);
          setEditingProduct(null);
        }
      } catch (error) {
        toast.error(editingProduct ? 'Failed to update' : 'Failed to create');
        throw error;
      }
    },
    [editingProduct, createMutation, updateMutation]
  );

  // Handler for printing a label from the table
  const handlePrintLabel = useCallback((product: Product) => {
    setProductToPrint(product);
    setShowQuickPrint(true);
  }, []);

  // Handler for editing in LabelForge
  const handleEditInLabelForge = useCallback((product: Product) => {
    // Navigate to LabelForge with product data in state
    navigate('/labels', { state: { importProduct: product } });
  }, [navigate]);

  // Handler for bulk printing labels
  const handleBulkPrintLabels = useCallback(() => {
    // Get the first selected product and open quick print
    const selectedProducts = products.filter((p) => selectedIds.includes(p.id));
    if (selectedProducts.length > 0) {
      setProductToPrint(selectedProducts[0]);
      setShowQuickPrint(true);
      toast.success(`Printing label for ${selectedProducts[0].name}. ${selectedProducts.length - 1} more in queue.`);
    }
  }, [products, selectedIds]);

  // Bulk action handlers
  const handleBulkActivate = async () => {
    await bulkUpdateMutation.mutateAsync({
      productIds: selectedIds,
      updates: { isActive: true },
    });
    toast.success(`${selectedIds.length} products activated`);
    setSelectedIds([]);
  };

  const handleBulkDeactivate = async () => {
    await bulkUpdateMutation.mutateAsync({
      productIds: selectedIds,
      updates: { isActive: false },
    });
    toast.success(`${selectedIds.length} products deactivated`);
    setSelectedIds([]);
  };

  const handleBulkDelete = async () => {
    await bulkDeleteMutation.mutateAsync(selectedIds);
    toast.success(`${selectedIds.length} products deleted`);
    setSelectedIds([]);
  };

  const handleBulkMove = async (storeId: string) => {
    await bulkMoveMutation.mutateAsync({ productIds: selectedIds, targetStoreId: storeId });
    toast.success(`${selectedIds.length} products moved`);
    setSelectedIds([]);
  };

  const handleBulkDuplicate = async () => {
    await bulkDuplicateMutation.mutateAsync(selectedIds);
    toast.success(`${selectedIds.length} products duplicated`);
    setSelectedIds([]);
  };

  const handleBulkCategory = async (categoryId: string | null) => {
    await bulkUpdateMutation.mutateAsync({
      productIds: selectedIds,
      updates: { categoryId },
    });
    toast.success(`Category updated for ${selectedIds.length} products`);
    setSelectedIds([]);
  };

  const handleBulkPrice = async (type: string, value: number) => {
    await bulkUpdateMutation.mutateAsync({
      productIds: selectedIds,
      updates: {
        priceAdjustment: { type: type as any, value },
      },
    });
    toast.success(`Price updated for ${selectedIds.length} products`);
    setSelectedIds([]);
  };

  // Import/Export handlers
  const handleImport = async (
    csvData: string,
    storeId?: string,
    categoryId?: string,
    updateExisting?: boolean
  ) => {
    const result = await importMutation.mutateAsync({
      csvData,
      storeId,
      categoryId,
      updateExisting,
    });
    return result;
  };

  const handleExport = async (
    storeId?: string,
    categoryId?: string,
    status?: string,
    format?: 'csv' | 'json'
  ) => {
    const result = await exportMutation.mutateAsync({
      storeId,
      categoryId,
      status: status as any,
      format,
    });
    return result;
  };

  const handleDownloadTemplate = async () => {
    const result = await downloadTemplateMutation.mutateAsync();
    return result as string;
  };

  const isBulkLoading =
    bulkUpdateMutation.isPending ||
    bulkDeleteMutation.isPending ||
    bulkMoveMutation.isPending ||
    bulkDuplicateMutation.isPending;

  return (
    <div className="max-w-7xl mx-auto space-y-4 lg:space-y-6">
      {/* Header - Mobile optimized */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3 lg:gap-4">
          <div className="p-2.5 lg:p-3 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600">
            <Package size={20} className="lg:w-6 lg:h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-slate-900">Products</h1>
            <p className="text-slate-500 text-sm">
              {total} product{total !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        {/* Action buttons - horizontal scroll on mobile */}
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar -mx-1 px-1">
          <button
            onClick={() => setShowImportExport(true)}
            className="btn btn-ghost shrink-0"
          >
            <Upload size={16} />
            <span className="hidden sm:inline">Import</span>
          </button>
          <button
            onClick={() => setShowImportExport(true)}
            className="btn btn-ghost shrink-0"
          >
            <Download size={16} />
            <span className="hidden sm:inline">Export</span>
          </button>
          <button
            onClick={() => {
              setEditingProduct(null);
              setShowForm(true);
            }}
            className="btn bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white shrink-0"
          >
            <Plus size={16} />
            <span>Add</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex gap-4 lg:gap-6">
        {/* Filters Sidebar (Desktop only) */}
        <div
          className={clsx(
            'hidden lg:block w-56 xl:w-64 shrink-0 transition-all',
            showFilters ? 'opacity-100' : 'lg:opacity-100'
          )}
        >
          <div className="card p-4 sticky top-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-slate-900 flex items-center gap-2 text-sm">
                <Filter size={16} />
                Filters
              </h3>
            </div>
            <ProductFilters
              filters={filters}
              onChange={setFilters}
              stores={stores}
              categories={categories}
            />
          </div>
        </div>

        {/* Products Table */}
        <div className="flex-1 min-w-0 card p-3 lg:p-4">
          <ProductsTable
            products={products}
            total={total}
            isLoading={isLoading}
            selectedIds={selectedIds}
            onSelectAll={handleSelectAll}
            onSelectOne={handleSelectOne}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onDuplicate={handleDuplicate}
            onPrintLabel={handlePrintLabel}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        </div>
      </div>

      {/* Mobile Filters */}
      <AnimatePresence>
        {showFilters && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 bg-black/70 z-40"
            onClick={() => setShowFilters(false)}
          >
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              className="absolute left-0 top-0 bottom-0 w-80 bg-white p-6 overflow-y-auto shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-medium text-slate-900">Filters</h3>
                <button onClick={() => setShowFilters(false)}>
                  <X size={24} className="text-slate-500 hover:text-slate-900" />
                </button>
              </div>
              <ProductFilters
                filters={filters}
                onChange={setFilters}
                stores={stores}
                categories={categories}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Filter Button - positioned above bottom nav */}
      <button
        onClick={() => setShowFilters(true)}
        className="lg:hidden fixed bottom-20 right-4 p-3.5 bg-orange-500 hover:bg-orange-600 active:bg-orange-700 rounded-full shadow-lg z-30"
      >
        <Filter size={20} className="text-white" />
      </button>

      {/* Product Form Modal */}
      <ProductForm
        product={editingProduct}
        stores={stores}
        categories={categories}
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingProduct(null);
        }}
        onSave={handleSave}
        isSaving={createMutation.isPending || updateMutation.isPending}
        defaultStoreId={defaultStoreId || filters.storeId}
      />

      {/* Import/Export Modal */}
      <ImportExport
        isOpen={showImportExport}
        onClose={() => setShowImportExport(false)}
        stores={stores}
        categories={categories}
        onImport={handleImport}
        onExport={handleExport}
        onDownloadTemplate={handleDownloadTemplate}
      />

      {/* Quick Print Modal */}
      <QuickPrintModal
        product={productToPrint}
        isOpen={showQuickPrint}
        onClose={() => {
          setShowQuickPrint(false);
          setProductToPrint(null);
        }}
        onEditInLabelForge={handleEditInLabelForge}
      />

      {/* Bulk Actions Bar */}
      <AnimatePresence>
        {selectedIds.length > 0 && (
          <BulkActionsBar
            selectedCount={selectedIds.length}
            onClear={() => setSelectedIds([])}
            onActivate={handleBulkActivate}
            onDeactivate={handleBulkDeactivate}
            onDelete={handleBulkDelete}
            onMove={handleBulkMove}
            onDuplicate={handleBulkDuplicate}
            onUpdateCategory={handleBulkCategory}
            onUpdatePrice={handleBulkPrice}
            onPrintLabels={handleBulkPrintLabels}
            stores={stores}
            categories={categories}
            isLoading={isBulkLoading}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
